// React auth state layer. Holds the current bearer token (initialised from
// localStorage so a refresh keeps the session) and exposes `login` / `logout`,
// plus the signed-in user's access picture (`me`): their account, effective
// permissions and company scope, loaded from `GET /api/auth/me`. Components
// gate what they *render* with `hasPermission` — the server still enforces
// every rule on each request, so this is presentation, not security.
//
// Components read it via the `useAuth` hook. The app gates on `isAuthenticated`
// (see `App.tsx`): unauthenticated renders the login screen. A 401 while
// loading `me` (expired token, deleted or blocked account) logs out.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { CurrentUser, PermissionCode } from '../domain';
import * as authApi from '../data/authClient';
import { loginWithPasskeyCeremony } from '../data/passkeyClient';
import { fetchMe } from '../data/apiClient';

export interface AuthContextValue {
  isAuthenticated: boolean;
  /** The signed-in user's account + derived access; null while loading. */
  me: CurrentUser | null;
  /** Whether the signed-in user effectively holds `permission` (false while loading). */
  hasPermission: (permission: PermissionCode) => boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Sign in with a passkey (the full WebAuthn ceremony; see passkeyClient). */
  loginWithPasskey: () => Promise<void>;
  /** Request a passwordless login link be emailed to `email` (see authClient). */
  requestLoginLink: (email: string) => Promise<void>;
  /** True when the address bar carries a magic-link token awaiting the user's click. */
  hasPendingLink: boolean;
  /** Redeem the pending magic-link token (invoked by an explicit user gesture). */
  redeemPendingLink: () => Promise<void>;
  /** True while the pending magic link is being redeemed. */
  linkVerifying: boolean;
  /** Error from a failed magic-link redemption (invalid / expired), or null. */
  linkError: string | null;
  logout: () => void;
  /** Re-fetch `me` — e.g. after edits that may have changed the caller's own grants. */
  refreshMe: () => Promise<void>;
  /**
   * True right after a password or magic-link sign-in — the cue for the
   * add-a-passkey prompt, which asks on every such login until the user has a
   * passkey (see `features/passkey-login.md`). Never set by a passkey sign-in
   * (they clearly have one on this device), and never by a mere page refresh.
   */
  passkeyPrompt: boolean;
  /** Dismiss the add-a-passkey prompt until the next fresh sign-in. */
  dismissPasskeyPrompt: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => authApi.getToken());
  const [me, setMe] = useState<CurrentUser | null>(null);
  // A magic link in the address bar (`/login?token=…`) is NOT redeemed on load.
  // Magic-link tokens are single-use, and JS-executing email security scanners
  // (Safe Links, Mimecast…) fetch and render links — auto-redeeming would let
  // one burn the token before the human clicks. So the token is held pending an
  // explicit user gesture (`redeemPendingLink`, wired to a button on the login
  // screen); a plain link-prefetcher, which never clicks, cannot consume it.
  const [pendingLinkToken, setPendingLinkToken] = useState<string | null>(() =>
    authApi.consumeLoginTokenFromUrl(),
  );
  const [linkVerifying, setLinkVerifying] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  // Set on a fresh password / magic-link sign-in only — a restored session
  // (page refresh) must not re-nag, and a passkey sign-in has nothing to nag
  // about on this device.
  const [passkeyPrompt, setPasskeyPrompt] = useState(false);

  // Bumped on every login/logout. A `refreshMe` captures the generation it
  // started in and only applies its result if still current, so an in-flight
  // `/auth/me` from a previous session can never repopulate `me` after logout
  // or after another account has signed in.
  const genRef = useRef(0);

  const login = useCallback(async (email: string, password: string) => {
    const issued = await authApi.login(email, password);
    genRef.current += 1;
    setMe(null); // never show the previous account's access while `me` reloads
    authApi.setToken(issued);
    setTokenState(issued);
    setPasskeyPrompt(true);
  }, []);

  const loginWithPasskey = useCallback(async () => {
    const issued = await loginWithPasskeyCeremony();
    genRef.current += 1;
    setMe(null);
    authApi.setToken(issued);
    setTokenState(issued);
  }, []);

  const requestLoginLink = useCallback(async (email: string) => {
    await authApi.requestLoginLink(email);
  }, []);

  // Redeem the pending magic-link token — driven by an explicit user click, not
  // on load (see `pendingLinkToken`). On success it starts a session just like
  // `login`; either way the token is cleared and stripped from the URL so a
  // refresh never re-submits a spent (single-use) link.
  const redeemPendingLink = useCallback(async () => {
    if (pendingLinkToken === null) return;
    setLinkVerifying(true);
    setLinkError(null);
    try {
      const issued = await authApi.verifyLoginLink(pendingLinkToken);
      genRef.current += 1;
      setMe(null);
      authApi.setToken(issued);
      setTokenState(issued);
      setPasskeyPrompt(true);
    } catch (error: unknown) {
      setLinkError(
        error instanceof Error
          ? error.message
          : 'This login link is invalid or has expired.',
      );
    } finally {
      setLinkVerifying(false);
      setPendingLinkToken(null);
      authApi.clearLoginTokenFromUrl();
    }
  }, [pendingLinkToken]);

  const logout = useCallback(() => {
    genRef.current += 1;
    authApi.clearToken();
    setTokenState(null);
    setMe(null);
    setLinkError(null);
    setPendingLinkToken(null);
    setPasskeyPrompt(false);
  }, []);

  const dismissPasskeyPrompt = useCallback(() => setPasskeyPrompt(false), []);

  const refreshMe = useCallback(async () => {
    const gen = genRef.current;
    try {
      const next = await fetchMe();
      if (gen === genRef.current) setMe(next);
    } catch (error: unknown) {
      // An unauthorized `me` means the session is dead (expired token, account
      // deleted or blocked): drop to the login screen. Other errors (network
      // blips) leave the current picture in place. Ignore a late failure from a
      // superseded session.
      if (gen === genRef.current && error instanceof authApi.UnauthorizedError) {
        logout();
      }
    }
  }, [logout]);

  // Any 401 from an API call (expired token, or a user blocked/deleted
  // mid-session) drops the whole app to the login screen — not only the
  // `/auth/me` refresh below.
  useEffect(() => {
    authApi.setUnauthorizedHandler(logout);
    return () => authApi.setUnauthorizedHandler(null);
  }, [logout]);

  // (Re)load the access picture whenever a session starts — on login and on a
  // refresh that restored a persisted token.
  useEffect(() => {
    if (token !== null) {
      void refreshMe();
    }
  }, [token, refreshMe]);

  const hasPermission = useCallback(
    (permission: PermissionCode) => me?.permissions.includes(permission) ?? false,
    [me],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: token !== null,
      me,
      hasPermission,
      login,
      loginWithPasskey,
      requestLoginLink,
      hasPendingLink: pendingLinkToken !== null,
      redeemPendingLink,
      linkVerifying,
      linkError,
      logout,
      refreshMe,
      passkeyPrompt,
      dismissPasskeyPrompt,
    }),
    [
      token,
      me,
      hasPermission,
      login,
      loginWithPasskey,
      requestLoginLink,
      pendingLinkToken,
      redeemPendingLink,
      linkVerifying,
      linkError,
      logout,
      refreshMe,
      passkeyPrompt,
      dismissPasskeyPrompt,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access auth state and actions. Must be used within an `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
